function removeEmptySpecificTags(html: string): string {
	const tagsToClean = ["b", "i", "ul", "ol", "li", "p"];

	const doc = new DOMParser().parseFromString(html, "text/html");

	function isEmptyNode(node: HTMLElement): boolean {
		// Ignore whitespace-only text nodes
		const textContent = node.textContent?.trim() ?? "";

		// Check if node only contains <br> elements (possibly with attributes)
		const onlyBrChildren = Array.from(node.childNodes).every((child) => {
			if (child.nodeType === Node.ELEMENT_NODE) {
				const el = child as HTMLElement;
				return el.tagName.toLowerCase() === "br";
			}
			// text nodes: only whitespace allowed
			return child.textContent?.trim() === "";
		});

		// Empty if no text and either no children or only <br>s
		return textContent === "" && (node.children.length === 0 || onlyBrChildren);
	}

	function cleanNode(node: HTMLElement) {
		// Recursively clean children first
		Array.from(node.children).forEach(cleanNode);

		// Remove node if:
		// - tag is in target list
		// - and node is empty per rules above
		if (tagsToClean.includes(node.tagName.toLowerCase()) && isEmptyNode(node)) {
			node.remove();
		}
	}

	Array.from(doc.body.children).forEach(cleanNode);

	return doc.body.innerHTML;
}

export const IsNullOrEmptyHtml = (htmlString?: string): boolean => {
	if (!htmlString || htmlString.length === 0) {
		return true;
	}

	// Check if the string is null, undefined, or empty after removing empty tags
	const cleanedString = removeEmptySpecificTags(htmlString);
	const isEmpty = !cleanedString || cleanedString.trim() === "";
	return isEmpty;
};

export const LengthOfHtmlTextString = (htmlString?: string): number => {
	if (!htmlString || htmlString.length === 0) {
		return 0;
	}

	const cleanedString = removeEmptySpecificTags(htmlString)?.trim();
	const isEmpty = !cleanedString || cleanedString === "";
	return isEmpty ? 0 : cleanedString.length;
};

export function htmlLineCount(html: string | null | undefined): number {
	if (!html) {
		return 0;
	}

	// Create a temporary DOM container
	const div = document.createElement("div");
	div.innerHTML = html;

	let lines = 0;

	// Count <p> as lines
	const paragraphs = div.querySelectorAll("p");
	lines += paragraphs.length;

	// Count <br> as individual breaks
	const brs = div.querySelectorAll("br");
	lines += brs.length;

	// If no <p> and no <br>, treat whole thing as 1 line
	if (lines === 0 && div.textContent?.trim()) {
		lines = 1;
	}

	return lines;
}

export const IsEqualIgnoreCase = (
	firstString: string,
	secondString: string
): boolean => {
	return firstString?.toUpperCase() === secondString?.toUpperCase();
};

interface TruncateOptions {
	maxLines?: number;
	maxCharacters?: number;
}

interface TruncateResult {
	value?: string;
	wasTruncated: boolean;
}

export function truncateHtml(
	html: string,
	options: TruncateOptions = {}
): TruncateResult {
	const { maxLines = 3, maxCharacters = 150 } = options;

	if (!html) {
		return { value: "", wasTruncated: false };
	}

	const container = document.createElement("div");
	container.innerHTML = html;

	let linesCount = 0;
	let charCount = 0;
	let truncated = false;

	function traverse(node: ChildNode): string | null {
		if (linesCount >= maxLines || charCount >= maxCharacters) {
			truncated = true;
			return null;
		}

		if (node.nodeType === Node.TEXT_NODE) {
			let text = node.textContent ?? "";
			const remainingChars = maxCharacters - charCount;

			if (text.length > remainingChars) {
				text = text.slice(0, remainingChars);
				truncated = true;
			}

			charCount += text.length;
			return text || null;
		}

		if (!(node instanceof Element)) {
			return null;
		}

		const tagName = node.tagName.toLowerCase();
		let result = "";

		const isLineTag = ["p", "br", "li"].includes(tagName);

		// <br> line counts as 1 line, no children
		if (tagName === "br") {
			if (linesCount >= maxLines) {
				truncated = true;
				return null;
			}
			linesCount++;
			return "<br/>";
		}

		// <p> or <li> line counts as 1 line
		if (isLineTag) {
			if (linesCount >= maxLines) {
				truncated = true;
				return null;
			}
			linesCount++;
		}

		// Traverse children
		const childrenHtml: string[] = [];
		const childNodes = Array.from(node.childNodes);
		for (const child of childNodes) {
			if (linesCount >= maxLines || charCount >= maxCharacters) {
				truncated = true;
				break;
			}
			const childHtml = traverse(child);
			if (childHtml) {
				childrenHtml.push(childHtml);
			}
		}

		// Reconstruct element with its children
		if (childrenHtml.length > 0 || tagName !== "br") {
			result = `<${tagName}${getAttributesString(node)}>${childrenHtml.join(
				""
			)}</${tagName}>`;
		} else {
			result = `<${tagName}${getAttributesString(node)}/>`;
		}

		return result;
	}

	function getAttributesString(el: Element): string {
		if (!el.attributes) {
			return "";
		}
		return Array.from(el.attributes)
			.map((attr) => ` ${attr.name}="${attr.value}"`)
			.join("");
	}

	const finalHtml: string[] = [];
	const nodes = Array.from(container.childNodes);
	for (const node of nodes) {
		if (linesCount >= maxLines || charCount >= maxCharacters) {
			truncated = true;
			break;
		}
		const htmlPart = traverse(node);
		if (htmlPart) {
			finalHtml.push(htmlPart);
		}
	}

	return { value: finalHtml.join(""), wasTruncated: truncated };
}
